import { copyFor, LOCALE_INFO, LOCALES, localizePath, stripWordJoiners, useCopy, useLocale } from '@/travo/i18n';
import { SITE_URL } from '@/shared/site';
import { ROUTES, type Route } from '@/travo/routes';
import { colors } from '@/travo/theme/colors';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  /** Absolute share-image URL; defaults to the locale's card in public/. */
  image?: string;
  /** Alt text for `image`; required in spirit whenever `image` is passed. */
  imageAlt?: string;
  imageWidth?: string;
  imageHeight?: string;
  /** Unlocalized page route. Canonical and hreflang URLs are built from it. */
  path?: Route;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const defaultSEO = {
  type: 'website',
  author: 'Travo Labs'
};

export const SEO = ({
  title,
  description,
  keywords,
  image,
  imageAlt,
  imageWidth = '1200',
  imageHeight = '630',
  path = ROUTES.home,
  type,
  author,
  publishedTime,
  modifiedTime
}: SEOProps) => {
  const locale = useLocale();
  const copy = useCopy();
  // Copy titles are complete (brand included), so no suffix is appended. Meta text is plain: no word joiners.
  const seoTitle = stripWordJoiners(title || copy.seo.home.title);
  const seoDescription = stripWordJoiners(description || copy.seo.home.description);
  const seoKeywords = stripWordJoiners(keywords || copy.seo.home.keywords);
  const seoImage = image || SITE_URL + copy.seo.ogImage;
  const seoImageAlt = stripWordJoiners(image ? (imageAlt ?? '') : copy.seo.ogImageAlt);
  const urlFor = (target: typeof locale): string => SITE_URL + localizePath(target, path);
  const seoUrl = urlFor(locale);
  const seoType = type || defaultSEO.type;
  const seoAuthor = author || defaultSEO.author;

  return (
    <>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={seoAuthor} />
      <link rel="canonical" href={seoUrl} />
      {/* A region tag (zh-Hant-TW) also gets its bare language tag (zh-Hant), so Hong Kong and Macau readers land here too */}
      {LOCALES.flatMap((target) =>
        [...new Set([copyFor(target).htmlLang, LOCALE_INFO[target].lang])].map((lang) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={urlFor(target)} />
        ))
      )}
      <link rel="alternate" hrefLang="x-default" href={urlFor('en')} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seoType} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:alt" content={seoImageAlt} />
      <meta property="og:site_name" content="Travo" />
      <meta property="og:locale" content={copy.ogLocale} />
      {LOCALES.filter((other) => other !== locale).map((other) => (
        <meta key={other} property="og:locale:alternate" content={copyFor(other).ogLocale} />
      ))}

      {/* Article specific meta tags */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={seoImageAlt} />
      <meta name="twitter:creator" content="@travo" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="language" content={copy.languageName} />
      <meta name="revisit-after" content="7 days" />

      {/* Theme Color */}
      <meta name="theme-color" content={colors.paper.value} />
      <meta name="msapplication-TileColor" content={colors.ink.value} />

      {/* Apple Meta Tags */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Travo" />
      <meta name="apple-itunes-app" content="app-id=6755287883" />
    </>
  );
};

export default SEO;
